import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const newsletterSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

interface NewsletterFormProps {
  lang: "en" | "fr";
  placeholder?: string;
  cta?: string;
}

export default function NewsletterForm({ 
  lang, 
  placeholder = "Your email address",
  cta = "Subscribe" 
}: NewsletterFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data: NewsletterFormData) => {
    try {
      setIsSubmitting(true);

      // Save to Firebase
      await addDoc(collection(db, "newsletter_subscriptions"), {
        email: data.email,
        subscribedAt: serverTimestamp(),
        language: lang,
        source: "blog",
      });

      toast({
        title: lang === "fr" ? "Inscription réussie!" : "Successfully subscribed!",
        description:
          lang === "fr"
            ? "Vous recevrez nos prochains articles par email."
            : "You'll receive our latest posts by email.",
      });

      reset();
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast({
        variant: "destructive",
        title: lang === "fr" ? "Erreur" : "Error",
        description:
          lang === "fr"
            ? "Une erreur s'est produite. Veuillez réessayer."
            : "An error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-4">
      <div className="flex-1">
        <Input
          type="email"
          placeholder={placeholder}
          {...register("email")}
          disabled={isSubmitting}
          className="w-full"
          aria-label={lang === "fr" ? "Adresse email" : "Email address"}
        />
        {errors.email && (
          <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
        )}
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="px-8 bg-gold hover:bg-gold/90 text-white"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {lang === "fr" ? "Envoi..." : "Sending..."}
          </>
        ) : (
          cta
        )}
      </Button>
    </form>
  );
}
